import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_OBJECT } from "@common/lib/consts";
import React from "react";
import { type BaseSelection } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import MenuButtonBase from "../Buttons/Base";
import { isMenuButtonActive } from "../lib";
import { type MenuButtonPropsBase } from "../types";
import { getActiveLink, upsertLink } from "./lib";
import LinkModal, { type LinkModalFormData } from "./Modal";

const LinkButton = (props: MenuButtonPropsBase) => {
  const i18n = useI18n();

  const editor = useSlate();

  const [data, setData] = React.useState<LinkModalFormData & { selection: BaseSelection }>();

  const onClick = React.useCallback(() => {
    const { selection } = editor;

    let text = "";
    if (selection) {
      const node = getActiveLink(editor);

      if (node) {
        setData({
          selection,
          isMailto: node[0].href?.startsWith("mailto") ?? false,
          isNewWindow: node[0].target === "_blank",
          text: editor.string(node[1]),
          url: node[0].href ?? ""
        });

        return;
      } else {
        text = editor.string(selection);
      }
    }

    setData({
      selection,
      isMailto: false,
      isNewWindow: false,
      text,
      url: ""
    });
  }, [editor.selection]);

  const submit = React.useCallback(
    (formData: LinkModalFormData) => {
      if (!data) return;

      ReactEditor.focus(editor);

      // https://app.clickup.com/t/86c3aqt16. Without setTimeout onChange event is not triggered in the editor
      setTimeout(() => upsertLink(editor, formData), 0);

      setData(undefined);
    },
    [data?.selection]
  );

  const onClose = React.useCallback(() => {
    ReactEditor.focus(editor);
    if (data?.selection) {
      editor.select(data.selection);
    }
    setData(undefined);
  }, [data?.selection]);

  return (
    <>
      <MenuButtonBase
        icon={IconType.Link}
        isActive={isMenuButtonActive(editor, "a")}
        isDisabled={props.isDisabled}
        onMouseDown={onClick}
      />
      <LinkModal
        className={props.dropdownClassName}
        isVisible={!!data}
        defaultValues={data ?? EMPTY_OBJECT}
        onClose={onClose}
        submit={submit}
        title={data?.url ? i18n.updateLink : i18n.insertLink}
      />
    </>
  );
};
export default LinkButton;
